import React, { useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { isEmpty, isNil, omit } from 'lodash'
import { useLocation } from 'react-router-dom'

import {
  removeFilterAndFetchCalls,
  removeFilters,
  clearAllFiltersAndFetchCalls,
  clearAllFilters,
  fetchCallsFromLatestFilters,
} from '@/reducers/callSearch/callSearch.actions'
import { setFilters, setData } from '@/reducers/callSearch/callSearch.redux'
import { DateRangePickerReusable } from '@/components/datePickers/DateRangePickerReusable'
import { FilterTypeDropdown } from '@/components/filters/types/FilterTypeDropdown'
import {
  getOrganizationOptions,
  getHierarchyOrganizationOptions,
  getChannelTypeOptions,
  hasAnyOmnichannelAccess,
} from '@/utils/helpers'
import { FilterPill } from '@/components/filters/FilterPill'
import { FilterTypeSelect } from '@/components/filters/types/FilterTypeSelect'
import { FilterTypeMultiSelect } from '@/components/filters/types/FilterTypeMultiSelect'
import {
  getInteractiveFilterConfig,
  dateIcon,
  organizationIcon,
  CHANNEL_FILTER,
} from '@/views/Calls/CallSearch.constants'
import {
  getSelectedPlaybookCids,
  buildBitFieldOptions,
  isBitFieldOptionDisabled,
  renderBitFieldOption,
  isBooleanBitField,
  BOOLEAN_FIELD_OPTIONS,
} from '@/views/Calls/bitFieldFilter.helpers'

const CallSearchPillsComponent = ({
  hiddenFilters = [],
  onApplyFilters,
  filterValuesApplied,
  handleSubmitFilters,
  handleClearRange,
  handleUpdateRange,
  dateInvalid,
  modalOpen,
  handleOpenFilterModal,
  allow120Days,
}) => {
  const dispatch = useDispatch()
  // see CallSearchFilters: fires only where Call Explorer refetches
  const applyFilters = () => onApplyFilters?.()
  const { organizations } = useSelector((state) => state)
  const { filters, loading, data } = useSelector((state) => state.callSearch)
  const { organizationid: currentUserOrgId, hierarchy_manager: isHierarchyManager } = useSelector(
    (state) => state.currentUser
  )
  const { active_channels: activeChannels = [] } = useSelector((state) => state.currentOrganization)
  const isBaltoAdmin = currentUserOrgId === 1
  const showManagerHierarchyOrgDropdown = !isBaltoAdmin && isHierarchyManager
  const { flattenedUserOrgHierarchy } = useSelector((state) => state.orgHierarchy)
  const hasOrganizationDropdown = isBaltoAdmin || showManagerHierarchyOrgDropdown
  const organizationOptions = getOrganizationOptions(organizations)
  const hierarchyOrganizationOptions = getHierarchyOrganizationOptions(flattenedUserOrgHierarchy)
  const location = useLocation()
  const isCallExplorerPage = location.pathname.startsWith('/explorer')

  const orgActiveChannels = useMemo(() => {
    if (isBaltoAdmin && filters.organizationId) {
      const selectedOrg = organizations?.find((org) => org.id === filters.organizationId)
      return selectedOrg?.active_channels || []
    }
    return activeChannels
  }, [isBaltoAdmin, filters.organizationId, organizations, activeChannels])

  const channelTypeOptions = getChannelTypeOptions(orgActiveChannels)

  const interactiveFilterConfig = getInteractiveFilterConfig(data, loading)

  const initialPlaybookEventsFilters = { includes: true, selected: [] }

  const handleRemoveFilter = (accessor) => {
    if (accessor === 'scorecards') {
      dispatch(setFilters({ scoredStatus: '', scorecardThreshold: '' }))
    }

    if (accessor === 'playbooks') {
      dispatch(setData({ playbookData: [] }))
      dispatch(
        setFilters({
          checklist: initialPlaybookEventsFilters,
          notifications: initialPlaybookEventsFilters,
          deck: initialPlaybookEventsFilters,
          postcall: initialPlaybookEventsFilters,
          openCategories: initialPlaybookEventsFilters,
          openSubcategories: initialPlaybookEventsFilters,
        })
      )
    }

    if (isCallExplorerPage) {
      dispatch(removeFilterAndFetchCalls(accessor))
    } else {
      dispatch(removeFilters(accessor))
      applyFilters()
    }
  }

  const handleMultiSelectUpdate = (accessor, value) => {
    if (isEmpty(value)) {
      handleRemoveFilter(accessor)
    } else {
      dispatch(setFilters({ [accessor]: value }))
    }
  }

  const handleSelectUpdate = (accessor, value) => {
    if (value === '') {
      handleRemoveFilter(accessor)
    } else {
      dispatch(setFilters({ [accessor]: value }))

      if (isCallExplorerPage) {
        dispatch(fetchCallsFromLatestFilters())
      }
      applyFilters()
    }
  }

  const handleApplyFilters = () => {
    if (isCallExplorerPage) {
      dispatch(fetchCallsFromLatestFilters())
    }
    applyFilters()
  }

  // Notetaker (bit-field) filters live under filters.bitFields keyed by field_id
  // and aren't part of the shared filter-display config, so surface each one
  // with an active selection as its own editable pill.
  const activeBitFields = data.bitFields.filter(
    (field) => !isBooleanBitField(field) && !isEmpty(filters.bitFields?.[field.field_id])
  )
  const activeBooleanFields = data.bitFields.filter(
    (field) => isBooleanBitField(field) && !isNil(filters.booleanFields?.[field.field_id])
  )
  const selectedPlaybookCids = getSelectedPlaybookCids(filters)

  const handleBitFieldUpdate = (fieldId, value) => {
    const nextBitFields = isEmpty(value)
      ? omit(filters.bitFields, fieldId)
      : { ...filters.bitFields, [fieldId]: value }
    dispatch(setFilters({ bitFields: nextBitFields }))
  }

  const handleRemoveBitField = (fieldId) => {
    dispatch(setFilters({ bitFields: omit(filters.bitFields, fieldId) }))
    if (isCallExplorerPage) {
      dispatch(fetchCallsFromLatestFilters())
    }
    applyFilters()
  }

  // FilterTypeSelect passes '' on clear; anything else is the chosen true/false.
  const handleBooleanFieldUpdate = (fieldId, value) => {
    const nextBooleanFields =
      value === ''
        ? omit(filters.booleanFields, fieldId)
        : { ...filters.booleanFields, [fieldId]: value }
    dispatch(setFilters({ booleanFields: nextBooleanFields }))
    if (isCallExplorerPage) {
      dispatch(fetchCallsFromLatestFilters())
    }
    applyFilters()
  }

  return (
    <>
      {hasOrganizationDropdown && (
        <FilterTypeSelect
          isClearable={filters.organizationId !== currentUserOrgId}
          loading={loading?.calls}
          schema={{
            accessor: 'organizationId',
            label: 'Organization',
            options: showManagerHierarchyOrgDropdown
              ? hierarchyOrganizationOptions
              : organizationOptions,
          }}
          updateSearch={(accessor, selectedOrgId) => {
            // if user is on the call explorer page we need to re-fetch calls w/ no filters applied, otherwise just clear the filters
            if (isCallExplorerPage) {
              if (!selectedOrgId) {
                dispatch(clearAllFiltersAndFetchCalls())
              } else {
                dispatch(clearAllFiltersAndFetchCalls(selectedOrgId))
              }
            } else if (!selectedOrgId) {
              dispatch(clearAllFilters())
            } else {
              dispatch(clearAllFilters(selectedOrgId))
            }
            applyFilters()
          }}
          value={filters.organizationId}
          customFilterIcon={organizationIcon}
        />
      )}
      {hasAnyOmnichannelAccess(orgActiveChannels, isBaltoAdmin) &&
        !isCallExplorerPage &&
        !hiddenFilters.includes(CHANNEL_FILTER) && (
          <FilterTypeDropdown
            schema={{ accessor: 'channel', label: 'Channel', options: channelTypeOptions }}
            value={filters.channel}
            updateSearch={(accessor, value) => dispatch(setFilters({ channel: value }))}
          />
        )}
      <DateRangePickerReusable
        withButtons
        icon={dateIcon}
        startDate={filters.startDate}
        endDate={filters.endDate}
        pickedPreset={filters.dateRange ?? null}
        cancelLabel="Clear"
        onCancel={handleClearRange}
        onChange={handleUpdateRange}
        onSave={handleSubmitFilters}
        shouldNotResetOnCancel
        saveLabel="Apply"
        error={dateInvalid}
        disableSaveButton={dateInvalid}
        isFilterPill
        friendlyLabel
        basicLabel
        modalOpen={modalOpen}
        allow120Days={allow120Days}
        saveOrCloseOnClickPreset
      />
      {!isEmpty(filterValuesApplied) &&
        filterValuesApplied.map(([accessor, properties]) => {
          const config = interactiveFilterConfig[accessor]

          if (config?.component === 'dropdown') {
            return (
              <FilterTypeDropdown
                key={accessor}
                isClearable
                schema={{ accessor, label: properties.label, options: config.options }}
                value={filters[accessor]}
                loading={config.loading}
                updateSearch={handleSelectUpdate}
                customFilterIcon={config.icon}
              />
            )
          }

          if (config?.component === 'select') {
            return (
              <FilterTypeSelect
                key={accessor}
                schema={{ accessor, label: properties.label, options: config.options }}
                value={filters[accessor]}
                loading={config.loading}
                updateSearch={handleSelectUpdate}
                customFilterIcon={config.icon}
              />
            )
          }

          if (config?.component === 'multiSelect') {
            return (
              <FilterTypeMultiSelect
                key={accessor}
                schema={{ accessor, label: properties.label, options: config.options }}
                value={filters[accessor]}
                loading={config.loading}
                isSelectAll={config.isSelectAll}
                showFullOptions={false}
                condenseOptions
                updateSearch={handleMultiSelectUpdate}
                onRemove={handleRemoveFilter}
                onClose={handleApplyFilters}
                customFilterIcon={config.icon}
              />
            )
          }

          return (
            <FilterPill
              key={accessor}
              accessor={accessor}
              handleRemoveFilter={() => handleRemoveFilter(accessor)}
              handleOpenFilterModal={handleOpenFilterModal}
              clickable
              loading={loading?.calls}
              customFilterIcon={config?.icon}
              {...properties}
            />
          )
        })}
      {activeBitFields.map((field) => (
        <FilterTypeMultiSelect
          key={field.field_id}
          schema={{
            accessor: field.field_id,
            label: field.field_name,
            options: buildBitFieldOptions(field, selectedPlaybookCids),
          }}
          value={filters.bitFields[field.field_id]}
          showFullOptions={false}
          condenseOptions
          isOptionDisabled={isBitFieldOptionDisabled}
          formatOptionLabel={renderBitFieldOption}
          updateSearch={handleBitFieldUpdate}
          onRemove={handleRemoveBitField}
          onClose={handleApplyFilters}
        />
      ))}
      {activeBooleanFields.map((field) => (
        <FilterTypeSelect
          key={field.field_id}
          schema={{
            accessor: field.field_id,
            label: field.field_name,
            options: BOOLEAN_FIELD_OPTIONS,
          }}
          value={filters.booleanFields[field.field_id]}
          updateSearch={handleBooleanFieldUpdate}
        />
      ))}
    </>
  )
}

export const CallSearchPills = CallSearchPillsComponent
